import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Modal from 'ui-library/modals/Modal';

import PlanPill, {PlanPillVariant} from './PlanPill';
import QueryErrorHandlingUnitOpLinkingEditor from './unit-op-linking-flowchart/QueryErrorHandlingUnitOpLinkingEditor';
import {UnitOpLinkEdge} from './unit-op-linking-flowchart/UnitOpLinkingFlowchartUtils';
import useUnitOpLinkingState, {
  UnitOpLinkingStateStatus,
  UnitOperationFields,
} from './unit-op-linking-flowchart/useUnitOpLinkingState';

export interface AddPlanUnitOperationLinksModalProps {
  studyId: StrictString;
  procedureRunPlanName: StrictString;
  /** Destination PMEIs computed from the procedure run plan before promotion. */
  destinationPMEIs: Array<UnitOperationFields>;
  /** Previously saved plan links to initialize with */
  initialLinks: Array<UnitOpLinkEdge>;
  onRequestClose: () => void;
  /** Called with the plan links when user confirms */
  onRequestSubmit: (links: Array<UnitOpLinkEdge>) => void;
}

/**
 * Modal for creating unit operation links for a procedure run plan.
 * Uses the same editor flow as unit-operation linking on procedure runs.
 * Does NOT save to backend - returns plan links via onRequestSubmit.
 */
export default function AddPlanUnitOperationLinksModal(
  props: AddPlanUnitOperationLinksModalProps
): JSX.Element {
  const unitOpLinkingState = useUnitOpLinkingState(props.studyId, {pmeis: props.destinationPMEIs});
  const querySuccess = unitOpLinkingState.status === UnitOpLinkingStateStatus.SUCCESS;
  const [isInitialized, setIsInitialized] = React.useState(false);
  const setNewLinks = querySuccess ? unitOpLinkingState.setNewLinks : undefined;

  // Initialize newLinks with the saved plan links BEFORE rendering the editor.
  // For plans, initialLinks from the hook is always [] (no DB links for virtual PMEIs),
  // so we put ALL links (saved + user changes) in newLinks.
  React.useEffect(() => {
    if (querySuccess && !isInitialized && setNewLinks) {
      setNewLinks(props.initialLinks);
      setIsInitialized(true);
    }
  }, [querySuccess, isInitialized, setNewLinks, props.initialLinks]);

  function onSubmit(): void {
    if (querySuccess) {
      // For plans, all links are in newLinks (see useEffect above).
      // Fall back to props.initialLinks if newLinks hasn't been initialized yet.
      props.onRequestSubmit(unitOpLinkingState.newLinks ?? props.initialLinks);
    } else {
      props.onRequestClose();
    }
  }

  // Don't render the editor until we've initialized newLinks with the saved plan links.
  // This ensures the editor sees the correct initial state on its first render.
  const showEditor = querySuccess && isInitialized;

  return (
    <Modal
      extraLarge={true}
      bodyPadded={false}
      fullHeight={true}
      title={
        <div className={css(styles.title)}>
          <span>Link to existing unit operations</span>
          <PlanPill variant={PlanPillVariant.MODAL} />
        </div>
      }
      onRequestClose={props.onRequestClose}
      onRequestSubmit={onSubmit}
      buttons={[
        {text: 'Cancel', cancel: true, disabled: false},
        {
          text: 'Save',
          submit: true,
          disabled: !querySuccess,
          dataTestAttrs: {component: 'AddPlanUnitOperationLinksModal', element: 'submitButton'},
        },
      ]}
    >
      <div className={css(styles.modalBodyContainer)}>
        <div className={css(styles.planTitle)}>
          <span>Recipe Run Plan: {props.procedureRunPlanName}</span>
        </div>
        <div className={css(styles.flowchartWrapper)}>
          {showEditor ? (
            <QueryErrorHandlingUnitOpLinkingEditor unitOpLinkingState={unitOpLinkingState} />
          ) : (
            <QueryErrorHandlingUnitOpLinkingEditor
              unitOpLinkingState={{status: UnitOpLinkingStateStatus.QUERY_PENDING}}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  modalBodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  planTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'black',
    padding: '6px 16px',
    borderBottom: '1px solid #e2e3e5',
  },
  flowchartWrapper: {
    flexGrow: 1,
  },
});
